import React from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { ReportSource } from '../../hooks/useQuarterlyApi';

/**
 * Из чего собран отчёт. Это факт на момент сборки, а не текущее состояние: если
 * человек допишет пропущенное эссе позже, строка не станет ложью только потому,
 * что рядом стоит дата.
 */
export const QuarterlySources: React.FC<{
  sources: ReportSource[];
  generatedAt?: string | null;
  /** Может ли смотрящий что-то сделать с пропуском. Читателю чужого отчёта
   *  предлагать «напишите эссе и соберите заново» нечестно: он не может ни того,
   *  ни другого. */
  canEdit?: boolean;
}> = ({ sources, generatedAt, canEdit }) => {
  const { t, i18n } = useTranslation();
  if (!generatedAt || !sources?.length) return null;

  const lang = i18n.language || 'ru';
  const monthName = (s: ReportSource) =>
    dayjs()
      .locale(lang)
      .year(s.year)
      .month(s.month - 1)
      .format('MMMM');

  const list = sources.map((s) => t(`quarterly.source.${s.status}`, { month: monthName(s) }));
  const missing = sources.filter((s) => s.status === 'missing').map(monthName);

  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">
        {t('quarterly.sources', {
          date: dayjs(generatedAt).locale(lang).format('D MMMM'),
          list: list.join(', '),
        })}
      </Text>
      {/* Пропуск последнего месяца квартала ожидаем: отчёт сдают 25-го, а эссе
          за этот месяц пишут только в начале следующего. Но пропущен может быть
          и любой другой месяц — просто потому, что человек его не написал.
          Поэтому текст нейтральный: он говорит, чего не хватает, и что с этим
          делать, не объясняя причину, которой может и не быть. */}
      {canEdit && missing.length > 0 && (
        <Text size="xs" c="dimmed">
          {t('quarterly.missingHint', { months: missing.join(', '), count: missing.length })}{' '}
          <Anchor component={Link} to="/essays" size="xs">
            {t('quarterly.missingHintAction')}
          </Anchor>
        </Text>
      )}
    </Stack>
  );
};
